import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { PublicAnnouncementSummary } from '@/api/publicAnnouncements'

interface AnnouncementTableProps {
  rows: PublicAnnouncementSummary[]
  onRowClick: (id: number) => void
}

const NEW_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000

function isNew(createdAt: string): boolean {
  const t = Date.parse(createdAt)
  if (Number.isNaN(t)) return false
  return Date.now() - t < NEW_THRESHOLD_MS
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}.${m}.${day}`
}

function NewBadge({ label }: { label: string }) {
  return (
    <span
      aria-label={label}
      className="inline-flex items-center justify-center size-5 rounded-full bg-[#ff675e] text-white text-[10px] font-bold leading-none"
    >
      N
    </span>
  )
}

export function AnnouncementTable({ rows, onRowClick }: AnnouncementTableProps) {
  const { t } = useTranslation('announcements')

  return (
    <ul className="w-full">
      {rows.map((row) => (
        <li
          key={row.announcementId}
          onClick={() => onRowClick(row.announcementId)}
          tabIndex={0}
          role="link"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onRowClick(row.announcementId)
            }
          }}
          className="flex flex-col items-start gap-4 lg:flex-row lg:items-center lg:gap-4 p-6 border-b border-[#dadada] cursor-pointer hover:bg-gray-50 transition-colors"
        >
          <span
            className={cn(
              'shrink-0 inline-flex items-center justify-center rounded-lg font-medium tracking-[-0.6px]',
              'px-3 py-1 text-sm',
              'lg:px-4 lg:text-base lg:min-w-[60px]',
              row.isPinned
                ? 'bg-[#f3f8ff] text-[#4599ff]'
                : 'bg-[#f4f4f4] text-[#494949]',
            )}
          >
            {row.isPinned ? t('category.pinned') : t('category.normal')}
          </span>

          <span className="flex items-center gap-2 text-lg lg:text-xl font-medium text-[#131313] tracking-[-0.6px] lg:flex-1 lg:min-w-0">
            <span className="lg:truncate">{row.title}</span>
            {isNew(row.createdAt) && <NewBadge label={t('new')} />}
          </span>

          <span className="shrink-0 text-sm lg:text-base font-normal text-[#a0a0a0] tracking-[-0.6px] whitespace-nowrap">
            {formatDate(row.createdAt)}
          </span>
        </li>
      ))}
    </ul>
  )
}
